'use client'
import * as React from 'react'
import {
  Block,
  RootBlock,
  isRootBlock,
  BlockRelationship,
  commonBlocksCompoundIndexes,
  CommonBlocksCompoundIndexes,
  getChildrenKeys,
} from '@basehub/data/replicache/index'
import { invariant } from '@epic-web/invariant'
import { getTreeManager } from '@basehub/utils/tree/index'
import { useRepoReplicache } from '../use-repo-replicache'
import { useStore } from 'zustand'
import { useBlocksMapStore } from './blocks-map-store'
import { BlockIconStoreProvider } from '~/components/block-icon/store'
import { useStoreWithEqualityFn } from 'zustand/traditional'
import {
  WorkingTreeProps,
  WorkingTreeStore,
  createWorkingTreeStore,
  handleDiff,
} from './provider-util'

const DEBUG = true
const logger = (message: unknown) => {
  // eslint-disable-next-line no-console
  if (DEBUG) console.log(message)
}

export const WorkingTreeContext = React.createContext<WorkingTreeStore | null>(
  null
)

export const WorkingTreeStoreProvider = ({
  children,
  rootBlockId,
}: {
  children: React.ReactNode
  rootBlockId: string
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache

  const lastRevertedAtRef = React.useRef<string | null | undefined>(undefined)
  const [storeVersion, setStoreVersion] = React.useState<number>(0)

  const store = React.useMemo(() => {
    // leave this here so the useMemo re-computes
    storeVersion
    // eslint-disable-next-line react-compiler/react-compiler
    const lastRevertedAt = lastRevertedAtRef.current
    // eslint-disable-next-line react-compiler/react-compiler
    return createWorkingTreeStore({ rootBlockId, lastRevertedAt })
  }, [rootBlockId, storeVersion])

  React.useEffect(() => {
    if (!replicache) return
    const unsub = replicache.subscribe((tx) => tx.get(`block/${rootBlockId}`), {
      onData: (_rootBlock) => {
        if (!_rootBlock) return
        const rootBlock = _rootBlock as RootBlock
        const shouldReCreateStore =
          lastRevertedAtRef.current !== undefined && // means unset
          lastRevertedAtRef.current !== rootBlock.lastRevertedAt
        lastRevertedAtRef.current = rootBlock.lastRevertedAt ?? null
        if (shouldReCreateStore) {
          setStoreVersion((v) => v + 1)
        }
      },
    })

    return unsub
  }, [replicache, rootBlockId])

  React.useEffect(() => {
    if (!replicache) return

    const unsub = replicache.experimentalWatch(
      (diff) => {
        const newState = handleDiff(diff, {
          logger,
          rootBlockId,
          state: store.getState(),
        })
        if (newState) {
          queueMicrotask(() => {
            store.setState(newState)
          })
        }
      },
      { initialValuesInFirstDiff: true }
    )
    return unsub
  }, [
    replicache,
    store,
    rootBlockId,
    data?.spaceId,
    data?.repoSlug,
    data?.team.slug,
  ])

  const _getChildrenKeys = useStore(
    store,
    (state) => state.tree?.getChildrenKeys
  )

  return (
    <WorkingTreeContext.Provider value={store}>
      <BlockIconStoreProvider getChildrenKeys={_getChildrenKeys}>
        {children}
      </BlockIconStoreProvider>
    </WorkingTreeContext.Provider>
  )
}

export const WorkingTreeStoreProvider__PUBLIC = ({
  children,
  blocks,
  brs,
  rootBlockId,
}: {
  children: React.ReactNode
  blocks: Block[]
  brs: BlockRelationship[]
  rootBlockId: string
}) => {
  const store = React.useMemo(() => createWorkingTreeStore(), [])

  React.useEffect(() => {
    const bStore = useBlocksMapStore.getState()
    const blocksMap = bStore.blocksMap
    const brsMap = bStore.brsMap

    let rootBlock: RootBlock | undefined
    blocks.forEach((b) => {
      blocksMap.set(b.id, b)
      if (isRootBlock(b)) rootBlock = b
    })
    invariant(rootBlock, 'Root block not found')
    const blockRelationshipsMap = bStore.getBrsByRootId(rootBlock)
    brs.forEach((br) => {
      brsMap.set(br.id, br)
    })

    const tree = getTreeManager<Block, CommonBlocksCompoundIndexes>({
      getKey: (item) => item.id,
      getOrder: (key) => {
        const br = blockRelationshipsMap.get(key)
        invariant(br, `block relationship for ${key} not found`)
        return br.order
      },
      getValue: (key) => {
        const block = blocksMap.get(key)
        invariant(block, `block ${key} not found in cache`)
        return block
      },
      getChildrenKeys: (_key, value) => {
        invariant(rootBlock, 'Root block not found')
        return getChildrenKeys({
          block: value,
          blockRelationshipsMap,
          blocksMap,
          rootBlockId: rootBlock.id,
        })
      },
      rootKey: rootBlock.id,
      indexKeys: ['sourceBlockId'],
      autoFallbackWithKeys: ['sourceBlockId'],
      compoundIndexes: commonBlocksCompoundIndexes,
    })

    store.setState({
      tree,
      treeRef: { current: tree },
      rootBlockId: rootBlock.id,
    })
  }, [blocks, brs, rootBlockId, store])

  const _getChildrenKeys = useStore(
    store,
    (state) => state.tree?.getChildrenKeys
  )

  return (
    <WorkingTreeContext.Provider value={store}>
      <BlockIconStoreProvider getChildrenKeys={_getChildrenKeys}>
        {children}
      </BlockIconStoreProvider>
    </WorkingTreeContext.Provider>
  )
}

export function useWorkingTreeStoreRoot() {
  const store = React.useContext(WorkingTreeContext)
  if (!store) throw new Error('Missing WorkingTreeContext.Provider in the tree')
  return store
}

export function useWorkingTreeStore<T>(
  selector: (state: WorkingTreeProps) => T,
  compare?: (prev: T, next: T) => boolean
): T {
  const store = useWorkingTreeStoreRoot()
  return useStoreWithEqualityFn(store, selector, compare)
}
